import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { useLang } from "@/i18n";

const PATHS = ["/marketing-branding", "/fiere-eventi-lanci", "/servizi", "/case-study", "/chi-sono", "/check-up", "/contatti"];

const LangToggle = ({ className = "" }) => {
  const { lang, setLang } = useLang();
  return (
    <div className={`flex items-center rounded-md border border-ink/15 ${className}`} data-testid="lang-toggle">
      {["it", "en"].map((l) => (
        <button
          key={l}
          type="button"
          onClick={() => setLang(l)}
          data-testid={`lang-toggle-${l}`}
          className={`px-2.5 py-1.5 font-mono text-[11px] uppercase tracking-widest transition-colors duration-200 ${
            lang === l ? "rounded-[5px] bg-ink text-white" : "text-mutedink hover:text-ink"
          }`}
        >
          {l}
        </button>
      ))}
    </div>
  );
};

const Header = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { d } = useLang();
  const labels = [d.nav.mb, d.nav.fiere, d.nav.services, d.nav.cs, d.nav.about, d.nav.checkup, d.nav.contact];

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header className="sticky top-0 z-50 border-b border-ink/10 bg-paper/90 backdrop-blur-xl" data-testid="site-header">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 sm:px-8 lg:px-12">
        <Link to="/" className="group flex flex-col leading-none" data-testid="header-logo-link">
          <span className="font-serif text-xl sm:text-2xl tracking-tight text-ink">
            Marketing <span className="italic text-accent">Facile</span>
          </span>
          <span className="mt-1 font-mono text-[10px] uppercase tracking-[0.3em] text-mutedink">
            Cristina Angelini
          </span>
        </Link>

        <nav className="hidden items-center gap-5 2xl:gap-7 xl:flex" data-testid="desktop-nav">
          {PATHS.map((path, i) => (
            <NavLink
              key={path}
              to={path}
              data-testid={`nav-link-${path === "/" ? "home" : path.slice(1)}`}
              className={({ isActive }) =>
                `text-[12px] 2xl:text-[13px] font-medium tracking-wide whitespace-nowrap transition-colors duration-200 ${
                  isActive ? "text-accent" : "text-ink/70 hover:text-ink"
                }`
              }
            >
              {labels[i]}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <LangToggle className="hidden sm:flex" />
          <Link
            to="/contatti"
            data-testid="nav-cta-parliamone-button"
            className="hidden items-center gap-2 rounded-md bg-accent px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] text-white whitespace-nowrap transition-colors duration-300 hover:bg-accent-dark sm:inline-flex"
          >
            {d.nav.cta}
            <ArrowUpRight className="h-3.5 w-3.5" />
          </Link>
          <button
            type="button"
            onClick={() => setOpen(!open)}
            data-testid="mobile-menu-button"
            aria-label="Menu"
            className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-ink/15 text-ink xl:hidden"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-t border-ink/10 bg-paper xl:hidden"
            data-testid="mobile-nav"
          >
            <div className="flex flex-col px-6 py-6">
              {PATHS.map((path, i) => (
                <NavLink
                  key={path}
                  to={path}
                  data-testid={`mobile-nav-link-${path === "/" ? "home" : path.slice(1)}`}
                  className={({ isActive }) =>
                    `flex items-baseline gap-4 border-b border-ink/8 py-4 font-serif text-2xl ${
                      isActive ? "text-accent" : "text-ink"
                    }`
                  }
                >
                  <span className="font-mono text-xs text-mutedink">0{i + 1}</span>
                  {labels[i]}
                </NavLink>
              ))}
              <div className="mt-6 flex items-center gap-3">
                <Link
                  to="/contatti"
                  data-testid="mobile-nav-cta-parliamone"
                  className="inline-flex flex-1 items-center justify-center gap-2 rounded-md bg-accent px-6 py-4 text-xs font-semibold uppercase tracking-[0.15em] text-white"
                >
                  {d.nav.cta}
                  <ArrowUpRight className="h-4 w-4" />
                </Link>
                <LangToggle />
              </div>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
